import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock, Eye, EyeSlash, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

/**
 * Account Settings — currently exposes the change-password flow.
 * Built as a self-contained dashboard card; future settings (display-name,
 * email-preferences, 2FA) plug in as additional sections.
 */
export default function AccountSettings() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!user) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (next !== confirm) {
            toast.error("New password and confirmation do not match");
            return;
        }
        if (next.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated. Future logins use your new password.");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Password update failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            data-testid="account-settings-section"
            className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8"
        >
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={16} weight="fill" className="text-electric-blue" />
                <p className="label-mono text-electric-blue">ACCOUNT SETTINGS</p>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-2">
                Security & <span className="text-electric-blue">password</span>
            </h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-xl">
                Change your password whenever you want — required at least once after your first login.
                Future restarts won't reset it.
            </p>

            <form onSubmit={submit} className="max-w-md space-y-4">
                <PasswordField
                    label="CURRENT PASSWORD"
                    value={current}
                    onChange={setCurrent}
                    show={showCurrent}
                    onToggle={() => setShowCurrent(!showCurrent)}
                    testId="settings-current-password"
                    autoComplete="current-password"
                />
                <PasswordField
                    label="NEW PASSWORD"
                    value={next}
                    onChange={setNext}
                    show={showNext}
                    onToggle={() => setShowNext(!showNext)}
                    hint="Min 8 characters · use a strong unique password"
                    testId="settings-new-password"
                    autoComplete="new-password"
                />
                <PasswordField
                    label="CONFIRM NEW PASSWORD"
                    value={confirm}
                    onChange={setConfirm}
                    show={showNext}
                    onToggle={() => setShowNext(!showNext)}
                    testId="settings-confirm-password"
                    autoComplete="new-password"
                />

                <button
                    type="submit"
                    disabled={loading || !current || !next || !confirm}
                    data-testid="settings-change-password-btn"
                    className="bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-5 py-3 text-xs tracking-wider uppercase transition-all hover:shadow-[0_0_20px_rgba(0,123,255,0.5)] flex items-center gap-2"
                >
                    {loading ? (
                        <>
                            <Lock size={14} weight="bold" className="animate-pulse" />
                            UPDATING...
                        </>
                    ) : (
                        <>
                            <CheckCircle size={14} weight="bold" />
                            UPDATE PASSWORD
                        </>
                    )}
                </button>
            </form>
        </section>
    );
}

function PasswordField({ label, value, onChange, show, onToggle, hint, testId, autoComplete }) {
    return (
        <div>
            <label className="label-mono text-[10px] text-muted-foreground block mb-1.5">
                {label}
                {hint && (
                    <span className="ml-2 text-[9px] text-white/40 normal-case tracking-normal">
                        — {hint}
                    </span>
                )}
            </label>
            <div className="relative">
                <input
                    data-testid={testId}
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    className="w-full bg-black/40 border border-white/10 px-3 py-2.5 pr-10 text-sm focus:border-electric-blue outline-none"
                    required
                />
                <button
                    type="button"
                    onClick={onToggle}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white p-1"
                    aria-label={show ? "Hide password" : "Show password"}
                >
                    {show ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
                </button>
            </div>
        </div>
    );
}
